import { GalleryInfosAction, GalleryInfosActionKeys } from './actions';
import { MultiAppAuthenticationInfosAction, MultiAppAuthenticationInfosActionKeys } from '../MultiAppAuthentication/actions';
import { Api } from '../../typings/api';
import { defaultTemplate } from '../../utils/styles-constants';
import Utils from '../../utils/index';

let initialState = {
	isConfigLoaded: false,
	isConfigFetching: false,
	isLocalTemplateLoaded: false,
	isLocalTemplateFetching: false,
	isRemoteTemplateLoaded: false,
	isRemoteTemplateFetching: false,
	forceViewUpdateCount: 0,
	forceSplashScreen: true,
	template: defaultTemplate,
	customCSS: '',
	customJS: '',
} as GalleryInfosState;

export interface GalleryInfosState {
	isConfigLoaded: boolean;
	isConfigFetching: boolean;
	isLocalTemplateLoaded: boolean;
	isLocalTemplateFetching: boolean;
	isRemoteTemplateLoaded: boolean;
	isRemoteTemplateFetching: boolean;
	config: Api.Config;
	template: Api.Template;
	customCSS: string;
	customJS: string;
	forceViewUpdateCount: number;
	forceSplashScreen: boolean;
}

export function galleryInfos(
	state: GalleryInfosState = initialState,
	action: GalleryInfosAction | MultiAppAuthenticationInfosAction
): GalleryInfosState {
	let propertiesToUpdate;
	let newState = {
		...state,
	};
	let newTemplate;
	let newCommonStyles;
	let customCSS;
	let customJS;
	switch (action.type) {
		case GalleryInfosActionKeys.REQUEST_CONFIG:
			return {
				...state,
				isConfigFetching: true,
			};
		case GalleryInfosActionKeys.RECEIVE_CONFIG:
			return {
				...state,
				isConfigFetching: false,
				isConfigLoaded: true,
				config: action.data,
			};
		case GalleryInfosActionKeys.REQUEST_LOCAL_TEMPLATE:
			return {
				...state,
				isLocalTemplateFetching: true,
			};
		case GalleryInfosActionKeys.RECEIVE_LOCAL_TEMPLATE:
			// Merge templates instead of replacing in case the remote template is incomplete
			// For local template, always merge with existing state (local template is only used for single-app)
			// and doesn't have the same cross-account contamination issue
			newTemplate = Utils.deepCopy(action.data);
			const baseCommonStylesForLocal = state.template.commonStyles
				? Utils.deepCopy(state.template.commonStyles)
				: Utils.deepCopy(defaultTemplate.commonStyles);

			newCommonStyles = baseCommonStylesForLocal;
			updateObjectProperties(newCommonStyles, newTemplate.commonStyles);
			newTemplate.commonStyles = newCommonStyles;
			return {
				...state,
				isLocalTemplateFetching: false,
				isLocalTemplateLoaded: true,
				template: newTemplate
			};
		case GalleryInfosActionKeys.SET_CUSTOM_CSS:
			customCSS = action.data;
			return {
				...state,
				customCSS
			};
		case GalleryInfosActionKeys.SET_CUSTOM_JS:
			customJS = action.data;
			return {
				...state,
				customJS
			};
		case GalleryInfosActionKeys.REQUEST_REMOTE_TEMPLATE:
			return {
				...state,
				isRemoteTemplateFetching: true,
			};
		case GalleryInfosActionKeys.RECEIVE_REMOTE_TEMPLATE:
			// Merge templates instead of replacing in case the remote template is incomplete
			// If userLoggedOut flag is true, start from defaultTemplate to prevent cross-account style contamination
			// Otherwise, merge with existing state to preserve cached templates when offline
			newTemplate = Utils.deepCopy(action.data);

			let baseCommonStyles;
			if (action.isAfterLogout) {
				// Fresh login after explicit logout - start from defaultTemplate to prevent cross-account style contamination
				baseCommonStyles = Utils.deepCopy(defaultTemplate.commonStyles);
				// If local template was loaded in this session, merge it into defaultTemplate first
				if (state.isLocalTemplateLoaded && state.template.commonStyles) {
					updateObjectProperties(baseCommonStyles, state.template.commonStyles);
				}
			} else {
				// Loading from cache (offline) or continuing session - merge with existing state
				// This preserves the cached template styles when user reopens app offline
				// The cached template already has merged styles, so we use it as the base
				baseCommonStyles = state.template.commonStyles
					? Utils.deepCopy(state.template.commonStyles)
					: Utils.deepCopy(defaultTemplate.commonStyles);
			}
			// Now merge remote template styles (which will override existing styles where they differ)
			// When loading from cache offline, this will merge the cached template with itself (no change)
			// When fetching fresh online, this will merge the fresh template with defaultTemplate or cached template
			newCommonStyles = baseCommonStyles;
			updateObjectProperties(newCommonStyles, newTemplate.commonStyles);
			newTemplate.commonStyles = newCommonStyles;
			return {
				...state,
				isRemoteTemplateFetching: false,
				isRemoteTemplateLoaded: true,
				template: newTemplate
			};
		case GalleryInfosActionKeys.FORCE_VIEW_UPDATE:
			return {
				...state,
				forceViewUpdateCount: state.forceViewUpdateCount + 1
			};
		case GalleryInfosActionKeys.SET_FORCE_SPLASH_SCREEN:
			return {
				...state,
				forceSplashScreen: action.isShown
			};
		case GalleryInfosActionKeys.UPDATE_TEMPLATE_BRAND:
			newState = Utils.deepCopy(state);
			propertiesToUpdate = action.value;

			if (newState.template.brand) {
				updateObjectProperties(newState.template.brand, propertiesToUpdate);
			}

			return newState;
		case GalleryInfosActionKeys.UPDATE_TEMPLATE_COMMONSTYLES:
			newState = Utils.deepCopy(state);
			propertiesToUpdate = action.value;

			if (newState.template.commonStyles) {
				updateObjectProperties(newState.template.commonStyles, propertiesToUpdate);
			}

			return newState;
		case MultiAppAuthenticationInfosActionKeys.LOGOUT:
			// Reset template to default to prevent styles from previous account persisting
			return {
				...initialState,
				// Preserve config if it exists (it might be needed for cleanup)
				config: state.config,
			};
		default:
			return state;
	}
}

function updateObjectProperties(object: {}, propertiesToUpdate: {}) {
	if (object) {
		for (let k in propertiesToUpdate) {
			if (propertiesToUpdate.hasOwnProperty(k)) {
				const updateValue = propertiesToUpdate[k];
				if (object[k] !== null && typeof object[k] === 'object') {
					object[k] = updateObjectProperties(object[k], updateValue);
				} else {
					object[k] = updateValue !== undefined ? updateValue : object[k];
				}
			}
		}
	}

	return object;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Gallery/reducer.ts